import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';

interface VersionInfo {
  version: string;
  buildTime: string;
  forceUpdate: boolean;
}

const STORAGE_KEY = 'app_version';
const POLL_INTERVAL = 60000; // 60 seconds (increased from 30s)

export const useAppUpdater = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>;

    const checkForUpdates = async () => {
      try {
        // Fetch version info with cache busting
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (!response.ok) return;

        const versionInfo: VersionInfo = await response.json();
        const storedVersion = localStorage.getItem(STORAGE_KEY);

        if (!storedVersion) {
          // First time - just store the version
          localStorage.setItem(STORAGE_KEY, versionInfo.version);
          return;
        }

        // Check if version has changed
        if (storedVersion !== versionInfo.version) {
          console.log(`Update detected: ${storedVersion} -> ${versionInfo.version}`);
          setUpdateAvailable(true);
          setForceUpdate(versionInfo.forceUpdate);
          
          // Update stored version
          localStorage.setItem(STORAGE_KEY, versionInfo.version);
          
          // If force update, reload immediately after a brief delay.
          // Guard with sessionStorage to prevent looping if the SW also triggers a reload.
          if (versionInfo.forceUpdate) {
            if (sessionStorage.getItem('force_update_reloading')) return;
            sessionStorage.setItem('force_update_reloading', '1');
            toast.info('Critical update available', {
              description: 'The app will refresh in a moment...',
              duration: 2000,
            });
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            // Normal update - show toast with refresh option instead of auto-reload
            toast.info('Update Available', {
              description: 'A new version is ready. Refresh to update.',
              action: {
                label: 'Refresh',
                onClick: handleRefresh,
              },
              duration: 10000, // Show for 10 seconds
            });
          }
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    };

    // Defer initial check to avoid blocking critical rendering path
    const initialCheckId = setTimeout(() => {
      checkForUpdates();
      // Then check every 60 seconds
      intervalId = setInterval(checkForUpdates, POLL_INTERVAL);
    }, 5000);

    return () => {
      clearTimeout(initialCheckId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [handleRefresh]);

  return { updateAvailable, forceUpdate, refresh: handleRefresh };
};